import React, { useEffect, useMemo, useState } from 'react'

import {
  Alert,
  Box,
  Card,
  CardContent,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material'
import { useFormik } from 'formik'

import RadioCardGroup from '../../../../shared/components/RadioCardGroup/RadioCardGroup.component'
import USPSLocationFinder from '../../../../shared/components/USPSLocationFinder/USPSLocationFinder.component'

import { getAirportZipCode } from '../../../../redux/states/flights/getAirportZipCode/getAirportZipCode.slice'
import { getDeliveryCountries } from '../../../../redux/states/offers/getDeliveryCountries/getDeliveryCountries.slice'
import { RootState } from '../../../../redux/store/store.types'
import date, { dateTimeFormats } from '../../../../shared/functions/Date/date.functions'
import { useAppDispatch, useAppSelector } from '../../../../shared/hooks/redux.hooks'
import { administrativeDivisionLabels, deliveryDetailsInitialValues } from './DeliveryDetails.dictionary'
import { US_ZIP_PLUS_FOUR_REGEX, buildDeliveryDetailsSchema } from './DeliveryDetails.schema'
import styles from './DeliveryDetails.styles'
import { DeliveryDetailsProps, DeliveryDetailsValues, ZipSource } from './DeliveryDetails.types'

const ZIP_PLUS_FOUR_HINT = 'ZIP+4 (e.g. 12345-6789) recommended — improves delivery accuracy.'
const ZIP_PLUS_FOUR_ENFORCED_HINT =
  'USPS could not confirm this address. Please provide the full ZIP+4 (e.g. 12345-6789) to continue.'

const ADDRESS_ONLY_FIELDS = ['streetAddress', 'country', 'administrativeDivision', 'city', 'postalCode'] as const

const ZIP_SOURCE_AIRPORT: ZipSource = 'airport'
const ZIP_SOURCE_CUSTOM: ZipSource = 'custom'

const DeliveryDetails: React.FC<DeliveryDetailsProps> = (props) => {
  const {
    onFormChange,
    sourceCountry,
    countriesAndCities = [],
    deliveryToTravelerWindow,
    arrivalAirportIataCode = null,
    mode = 'full',
    sectionTitle: sectionTitleProp,
    initialValues: initialValuesProp,
    isFulfillmentOrder = true,
    enforceZipPlus4 = false,
    suggestedZipPlus4 = null
  } = props
  const isStayAddressOnly = mode === 'stayAddressOnly'
  const addressSectionTitle = sectionTitleProp ?? (isStayAddressOnly ? 'Stay Address' : 'Delivery Address')
  const [selectedCountry, setSelectedCountry] = useState<string>('')

  const dispatch = useAppDispatch()
  const {
    loading: airportZipLoading,
    success: airportZipSuccess,
    error: airportZipError
  } = useAppSelector((state: RootState) => state.getAirportZipCode)

  // Helper function to get the appropriate label for administrative division
  const getAdministrativeDivisionLabel = (countryCode: string): string => {
    return administrativeDivisionLabels[countryCode] || 'State/Province/Region'
  }

  const [administrativeDivisionLabel, setAdministrativeDivisionLabel] = useState('State/Province/Region')

  // Fetch delivery countries on component mount
  useEffect(() => {
    dispatch(getDeliveryCountries({}))
  }, [dispatch])

  const initialValues: DeliveryDetailsValues = {
    ...deliveryDetailsInitialValues,
    ...(initialValuesProp && initialValuesProp)
  }

  const validationSchema = useMemo(
    () => buildDeliveryDetailsSchema({ administrativeDivisionLabel, enforceZipPlus4 }),
    [administrativeDivisionLabel, enforceZipPlus4]
  )

  const formik = useFormik({
    initialValues,
    validationSchema,
    // eslint-disable-next-line no-empty-function
    onSubmit: () => {},
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: Boolean(initialValuesProp)
  })

  const countryOptions = useMemo(() => {
    return countriesAndCities.map((country) => ({
      value: country.iataCountryCode,
      label: country.countryName !== 'Unknown' ? country.countryName : country.iataCountryCode
    }))
  }, [countriesAndCities])

  // Set source country when provided (pre-select in dropdown)
  useEffect(() => {
    if (sourceCountry?.iataCode) {
      setSelectedCountry(sourceCountry.iataCode)
      formik.setFieldValue('country', sourceCountry.iataCode)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Fetch airport ZIP when "airport" is selected and we have an arrival IATA code
  useEffect(() => {
    if (!isFulfillmentOrder) return

    if (arrivalAirportIataCode && formik.values.zipSource === ZIP_SOURCE_AIRPORT) {
      dispatch(
        getAirportZipCode({
          request: { iataCode: arrivalAirportIataCode }
        })
      )
    }
  }, [isFulfillmentOrder, arrivalAirportIataCode, formik.values.zipSource, dispatch])

  // When airport ZIP loads, set postalCode
  useEffect(() => {
    if (!isFulfillmentOrder) return

    if (formik.values.zipSource === ZIP_SOURCE_AIRPORT && airportZipSuccess?.data) {
      formik.setFieldValue('postalCode', airportZipSuccess.data.zipCode)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- omit formik to avoid effect on every form change
  }, [isFulfillmentOrder, formik.values.zipSource, airportZipSuccess])

  // When custom ZIP is selected, keep postalCode in sync with customPostalCode
  useEffect(() => {
    if (!isFulfillmentOrder) return

    if (formik.values.zipSource === ZIP_SOURCE_CUSTOM && formik.values.customPostalCode) {
      formik.setFieldValue('postalCode', formik.values.customPostalCode)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- omit formik to avoid effect on every form change
  }, [isFulfillmentOrder, formik.values.zipSource, formik.values.customPostalCode])

  // When USPS returns a suggested ZIP+4, prefill it onto the postalCode — but never overwrite an existing +4 the user typed.
  useEffect(() => {
    if (!suggestedZipPlus4) return

    const current = formik.values.postalCode ?? ''
    if (US_ZIP_PLUS_FOUR_REGEX.test(current)) return

    const fiveDigit = current.replace(/\D/g, '').slice(0, 5)
    if (fiveDigit.length !== 5) return

    const plus = suggestedZipPlus4.replace(/\D/g, '').slice(0, 4)
    if (plus.length !== 4) return

    const next = `${fiveDigit}-${plus}`
    if (next !== current) {
      formik.setFieldValue('postalCode', next)
      if (formik.values.zipSource === ZIP_SOURCE_CUSTOM) {
        formik.setFieldValue('customPostalCode', next)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- omit formik to avoid effect on every form change
  }, [suggestedZipPlus4])

  useEffect(() => {
    // Update administrative division label when country changes
    if (selectedCountry) {
      setAdministrativeDivisionLabel(getAdministrativeDivisionLabel(selectedCountry))
    } else {
      setAdministrativeDivisionLabel('State/Province/Region')
    }
  }, [selectedCountry])

  const getStayDateText = () => {
    if (deliveryToTravelerWindow) {
      const startDate = date(deliveryToTravelerWindow.startDate).format(dateTimeFormats.date.medium)
      const endDate = date(deliveryToTravelerWindow.endDate).format(dateTimeFormats.date.medium)

      return `Items will be delivered between ${startDate} and ${endDate}`
    }

    return 'Delivery window is based on departure to delivery country'
  }

  // Update parent component when form values or validity changes
  useEffect(() => {
    const values = formik.values

    const hasAllRequiredAddressFields = Boolean(
      values.streetAddress && values.city && values.administrativeDivision && values.country && values.postalCode
    )

    const addressOnlyValid =
      hasAllRequiredAddressFields &&
      ADDRESS_ONLY_FIELDS.every((field) => !formik.errors[field as keyof typeof formik.errors])

    if (isStayAddressOnly) {
      onFormChange(values, addressOnlyValid)

      return
    }

    const hasNoErrors = Object.keys(formik.errors).length === 0
    const isUsDestination = formik.values.country === 'US'
    const hasUspsSelectionWhenRequired = !isUsDestination || !isFulfillmentOrder || values.selectedUSPSLocation != null
    const isValid = hasAllRequiredAddressFields && hasNoErrors && hasUspsSelectionWhenRequired

    onFormChange(values, isValid)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- formik.values/errors are the actual deps; omit formik to avoid effect on every form change
  }, [formik.values, formik.errors, onFormChange, isStayAddressOnly, isFulfillmentOrder])

  return (
    <Card sx={styles.container}>
      <CardContent>
        {!isStayAddressOnly && (
          <Alert severity="info" sx={styles.warningBox}>
            <Typography sx={styles.warningText}>
              {deliveryToTravelerWindow
                ? `Please provide the delivery address. Items will be delivered to this address between ${date(deliveryToTravelerWindow.startDate).format(dateTimeFormats.date.medium)} and ${date(deliveryToTravelerWindow.endDate).format(dateTimeFormats.date.medium)} to ensure they arrive before your departure.`
                : 'Please provide the delivery address. Items will be delivered to this address within the calculated delivery window based on your flight departure date to ensure timely arrival.'}
            </Typography>
          </Alert>
        )}

        {!isStayAddressOnly && isFulfillmentOrder && (
          <>
            <Typography variant="h6" sx={styles.sectionTitle}>
              Where will you drop off your package?
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              USPS rates are the same for all locations in a ZIP code. We use your ZIP to find nearby drop-off locations
              and show accurate shipping costs.
            </Typography>

            <RadioCardGroup
              name="zipSource"
              value={formik.values.zipSource}
              onChange={(value) => {
                const nextZipSource = value as ZipSource
                formik.setFieldValue('zipSource', nextZipSource)
                if (nextZipSource === ZIP_SOURCE_CUSTOM) {
                  formik.setFieldValue('postalCode', formik.values.customPostalCode)
                }
              }}
              inputValue={formik.values.customPostalCode}
              onInputValueChange={(value) => {
                formik.setFieldValue('customPostalCode', value)
                if (formik.values.zipSource === ZIP_SOURCE_CUSTOM) {
                  formik.setFieldValue('postalCode', value)
                }
              }}
              direction="row"
              aria-label="ZIP code for USPS drop-off location"
              options={[
                {
                  value: ZIP_SOURCE_AIRPORT,
                  title: 'Airport ZIP code',
                  description: airportZipLoading
                    ? 'Loading…'
                    : airportZipSuccess?.data
                      ? `${airportZipSuccess.data.zipCode} (${airportZipSuccess.data.airportName})`
                      : airportZipError
                        ? 'This airport is not in our list. Use the other option to enter a custom ZIP.'
                        : arrivalAirportIataCode
                          ? 'Use the ZIP for your arrival airport'
                          : 'Select your arrival airport above first, or enter a custom ZIP.',
                  disabled: !arrivalAirportIataCode
                },
                {
                  value: ZIP_SOURCE_CUSTOM,
                  title: "I'll be staying at",
                  description: 'Enter the ZIP code where you will be staying',
                  renderInput: ({ value, onChange }) => (
                    <TextField
                      fullWidth
                      size="small"
                      label="ZIP code"
                      placeholder="Enter 5-digit ZIP"
                      value={value}
                      onChange={(event) => onChange(event.target.value.replace(/\D/g, '').slice(0, 5))}
                      onBlur={() => formik.setFieldTouched('customPostalCode', true)}
                      error={formik.touched.customPostalCode === true && Boolean(formik.errors.customPostalCode)}
                      helperText={formik.touched.customPostalCode === true ? formik.errors.customPostalCode : undefined}
                      inputProps={{ maxLength: 5 }}
                    />
                  )
                }
              ]}
            />
          </>
        )}

        <Typography variant="h6" sx={styles.sectionTitle}>
          {addressSectionTitle}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              name="streetAddress"
              label="Street Address"
              placeholder={
                isStayAddressOnly
                  ? 'Enter the street address where you will be staying'
                  : 'Enter the street address where items will be delivered'
              }
              value={formik.values.streetAddress}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.streetAddress && Boolean(formik.errors.streetAddress)}
              helperText={formik.touched.streetAddress && formik.errors.streetAddress}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl
              fullWidth
              required
              error={formik.touched.country && Boolean(formik.errors.country)}
              disabled={countryOptions.length === 0 || isStayAddressOnly || !isFulfillmentOrder}
            >
              <InputLabel id="delivery-details-country-label">Country</InputLabel>

              <Select
                labelId="delivery-details-country-label"
                id="delivery-details-country"
                name="country"
                value={formik.values.country}
                label="Country"
                onChange={(event) => {
                  const countryIataCode = event.target.value as string
                  setSelectedCountry(countryIataCode)
                  formik.setFieldValue('country', countryIataCode)
                }}
                onBlur={formik.handleBlur}
              >
                <MenuItem value="">
                  <em>Select country</em>
                </MenuItem>

                {countryOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>

              {formik.touched.country && formik.errors.country && (
                <FormHelperText>{formik.errors.country}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="administrativeDivision"
              label={administrativeDivisionLabel}
              placeholder={`Enter ${administrativeDivisionLabel.toLowerCase()}`}
              value={formik.values.administrativeDivision}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.administrativeDivision && Boolean(formik.errors.administrativeDivision)}
              helperText={formik.touched.administrativeDivision && formik.errors.administrativeDivision}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="city"
              label="City"
              placeholder="Enter city"
              value={formik.values.city}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.city && Boolean(formik.errors.city)}
              helperText={formik.touched.city && formik.errors.city}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="postalCode"
              label="Postal/Zip Code"
              placeholder="Enter postal or zip code"
              value={formik.values.postalCode}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                (formik.touched.postalCode && Boolean(formik.errors.postalCode)) ||
                (enforceZipPlus4 && formik.values.country === 'US')
              }
              helperText={
                formik.touched.postalCode && formik.errors.postalCode
                  ? formik.errors.postalCode
                  : formik.values.country === 'US'
                    ? enforceZipPlus4
                      ? ZIP_PLUS_FOUR_ENFORCED_HINT
                      : ZIP_PLUS_FOUR_HINT
                    : undefined
              }
              required
            />
          </Grid>
        </Grid>

        {/* USPS location selection (fulfillment orders, US destinations only); hidden in stayAddressOnly mode */}
        {!isStayAddressOnly && isFulfillmentOrder && formik.values.country === 'US' && formik.values.postalCode && (
          <Box sx={{ marginTop: 3 }}>
            <Typography variant="h6" sx={styles.sectionTitle}>
              USPS drop-off location
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ marginBottom: 2 }}>
              Select a USPS location where you will drop off your package. This is required for US destinations.
            </Typography>

            <USPSLocationFinder
              recipientZipCode={formik.values.postalCode}
              onLocationSelect={(locationID, locationData) =>
                formik.setFieldValue('selectedUSPSLocation', { locationID, locationData })
              }
              selectedLocationID={formik.values.selectedUSPSLocation?.locationID}
            />
          </Box>
        )}
      </CardContent>

      {!isStayAddressOnly && (
        <CardContent>
          <Typography variant="h6" sx={styles.sectionTitle}>
            Delivery Window
          </Typography>

          <Box sx={styles.datePickerBox(false)}>
            <Typography sx={styles.datePickerText(false)}>{getStayDateText()}</Typography>
          </Box>
        </CardContent>
      )}
    </Card>
  )
}

export default DeliveryDetails
